import type { DashboardDatePreset } from '@lognai/plugin-analytics';
import { createPersistedInsightJob, getPersistedInsightJob, type AnalysisResult } from './api';
import { CHART_INSIGHT_QUESTION } from './chart-insight-question';

export interface ChartInsightRefreshRequest {
  dashboardId: string;
  tabId?: string;
  widgetId: string;
  visualVersion: number;
  datePreset: DashboardDatePreset;
  result: AnalysisResult;
}

/** Execution IDs and request timings change on every reload; chart data defines freshness. */
export function chartInsightDataKey(result: AnalysisResult): string {
  return JSON.stringify({ analysis: result.analysis, visual: result.visual, evidence: result.evidence,
    query: result.trace.map(({ view, measures, dimensions, filters, rowCount }) => ({ view, measures, dimensions, filters, rowCount })),
    versions: result.context?.datasetVersions, internal: result.context?.internalFreshness, timezone: result.context?.timezone ?? 'UTC' });
}

const running = new Map<string, { key: string; promise: Promise<void> }>();
const completed = new Map<string, string>();

/** Share work between the chart and its modal, and serialize refreshes of the same chart. */
export async function refreshChartInsight(request: ChartInsightRefreshRequest, onComplete: () => Promise<void>, isCurrent: () => boolean): Promise<void> {
  const target = JSON.stringify([request.dashboardId, request.tabId, request.widgetId]);
  const key = chartInsightDataKey(request.result);
  if (!isCurrent() || completed.get(target) === key) return;
  const previous = running.get(target);
  if (previous) {
    if (previous.key === key) return previous.promise;
    await previous.promise.catch(() => undefined);
    return refreshChartInsight(request, onComplete, isCurrent);
  }
  const promise = (async () => {
    const job = await createPersistedInsightJob(request.dashboardId, {
      targetType: 'widget', tabId: request.tabId, widgetId: request.widgetId, question: CHART_INSIGHT_QUESTION,
      analysis: request.result.analysis,
      context: { datePreset: request.datePreset, filters: request.result.analysis.filters, drillState: [], visualVersion: request.visualVersion },
    });
    let current = job;
    for (let attempt = 0; attempt < 160 && !['complete', 'failed'].includes(current.status); attempt++) {
      await new Promise(resolve => window.setTimeout(resolve, 1000));
      current = await getPersistedInsightJob(request.dashboardId, job.id);
    }
    if (current.status === 'failed') throw new Error(current.error ?? 'The insight could not be refreshed.');
    if (current.status !== 'complete') throw new Error('The insight is still updating in the background.');
    completed.delete(target);
    completed.set(target, key);
    if (completed.size > 200) completed.delete(completed.keys().next().value!);
    await onComplete();
  })();
  running.set(target, { key, promise });
  try { await promise; } finally { if (running.get(target)?.promise === promise) running.delete(target); }
}
